import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import RuleConditionBuilder from '../rules/RuleConditionBuilder';
import UploadingModal from '../common/UploadingModal';
import { customDocumentService } from '../../services/api/customDocumentService';
import { customReportService } from '../../services/api/customReportService';
import type { DocumentTemplateSummary, DocumentOutputFormat, BatchGenerateRequest } from '../../types/customDocuments';
import type { ConditionGroup, FieldCatalogueEntry } from '../../types/rulesEngine';
import type { FilterOptions } from '../../types/customReports';

interface Props {
  clientId: string;
  templates: DocumentTemplateSummary[];
  onJobCreated: (result: { jobId: string; totalItems: number }) => void;
  onClose: () => void;
}

const BatchGenerateModal: React.FC<Props> = ({
  clientId,
  templates,
  onJobCreated,
  onClose,
}) => {
  const { t } = useTranslation();

  // Form state
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [outputFormat, setOutputFormat] = useState<DocumentOutputFormat>('PDF');
  const [conditionGroups, setConditionGroups] = useState<ConditionGroup[]>([]);
  const [overwriteExisting, setOverwriteExisting] = useState(false);

  // Count state
  const [matchCount, setMatchCount] = useState<number | null>(null);
  const [counting, setCounting] = useState(false);

  // Submit state
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filter options for field catalogue dropdowns
  const [filterOptions, setFilterOptions] = useState<FilterOptions | null>(null);

  // Reset count when conditions change
  useEffect(() => {
    setMatchCount(null);
  }, [conditionGroups]);

  // Load filter options on mount
  useEffect(() => {
    customReportService.getFilterOptions(clientId)
      .then(setFilterOptions)
      .catch(err => console.error('Failed to load filter options', err));
  }, [clientId]);

  // Only active templates
  const activeTemplates = useMemo(
    () => templates.filter(t => t.isActive),
    [templates]
  );

  // Build field catalogue from spec (13 fields)
  const fieldCatalogue: FieldCatalogueEntry[] = useMemo(() => {
    const stringOps: FieldCatalogueEntry['operators'] = ['equals', 'not_equals', 'in', 'not_in'];
    const dateOps: FieldCatalogueEntry['operators'] = ['equals', 'greater_than', 'less_than', 'between'];

    const toOptions = (values: string[]) =>
      values.map(v => ({ label: v, value: v }));

    return [
      {
        displayName: t('batch.fields.workflowState', 'Workflow State'),
        field: 'workflowState',
        valueType: 'string',
        operators: stringOps,
        inputType: 'dropdown',
        options: filterOptions
          ? toOptions(filterOptions.workflowStates)
          : toOptions(['Draft', 'Booked', 'Confirmed', 'ContractSigned', 'Final', 'Cancelled', 'Unwound']),
      },
      {
        displayName: t('batch.fields.tradeDate', 'Trade Date'),
        field: 'TradeDate',
        valueType: 'date',
        operators: dateOps,
        inputType: 'datepicker',
      },
      {
        displayName: t('batch.fields.productType', 'Product Type'),
        field: 'ProductType',
        valueType: 'string',
        operators: stringOps,
        inputType: 'dropdown',
        options: filterOptions
          ? toOptions(filterOptions.productTypes)
          : toOptions(['Spot', 'Forward']),
      },
      {
        displayName: t('batch.fields.direction', 'Direction'),
        field: 'Direction',
        valueType: 'string',
        operators: stringOps,
        inputType: 'dropdown',
        options: toOptions(['Buy', 'Sell']),
      },
      {
        displayName: t('batch.fields.settlementType', 'Settlement Type'),
        field: 'SettlementType',
        valueType: 'string',
        operators: stringOps,
        inputType: 'dropdown',
        options: toOptions(['Entrega Física', 'Compensación']),
      },
      {
        displayName: t('batch.fields.currency1', 'Currency 1'),
        field: 'Currency1',
        valueType: 'string',
        operators: stringOps,
        inputType: 'dropdown',
        options: filterOptions ? toOptions(filterOptions.currencies) : [],
      },
      {
        displayName: t('batch.fields.currency2', 'Currency 2'),
        field: 'Currency2',
        valueType: 'string',
        operators: stringOps,
        inputType: 'dropdown',
        options: filterOptions ? toOptions(filterOptions.currencies) : [],
      },
      {
        displayName: t('batch.fields.ourParty', 'Our Party'),
        field: 'OurParty',
        valueType: 'string',
        operators: stringOps,
        inputType: 'dropdown',
        options: filterOptions ? toOptions(filterOptions.ourPartyValues) : [],
      },
      {
        displayName: t('batch.fields.counterparty', 'Counterparty'),
        field: 'CounterpartyName',
        valueType: 'string',
        operators: stringOps,
        inputType: 'dropdown',
        options: filterOptions ? toOptions(filterOptions.counterparties) : [],
      },
      {
        displayName: t('batch.fields.isInternalTrade', 'Internal Trade'),
        field: 'isInternalTrade',
        valueType: 'string',
        operators: ['equals', 'not_equals'],
        inputType: 'dropdown',
        options: toOptions(['true', 'false']),
      },
      {
        displayName: filterOptions?.grouping1Label || t('batch.fields.grouping1', 'Grouping 1'),
        field: 'Grouping1',
        valueType: 'string',
        operators: stringOps,
        inputType: 'dropdown',
        options: filterOptions ? toOptions(filterOptions.grouping1Values) : [],
      },
      {
        displayName: filterOptions?.grouping2Label || t('batch.fields.grouping2', 'Grouping 2'),
        field: 'Grouping2',
        valueType: 'string',
        operators: stringOps,
        inputType: 'dropdown',
        options: filterOptions ? toOptions(filterOptions.grouping2Values) : [],
      },
      {
        displayName: filterOptions?.grouping3Label || t('batch.fields.grouping3', 'Grouping 3'),
        field: 'Grouping3',
        valueType: 'string',
        operators: stringOps,
        inputType: 'dropdown',
        options: filterOptions ? toOptions(filterOptions.grouping3Values) : [],
      },
    ];
  }, [t, filterOptions]);

  const handleCount = useCallback(async () => {
    setCounting(true);
    setError(null);
    try {
      const result = await customDocumentService.countMatchingTrades(clientId, conditionGroups);
      setMatchCount(result.count);
    } catch {
      setError(t('batch.countError', 'Failed to count matching trades'));
    } finally {
      setCounting(false);
    }
  }, [clientId, conditionGroups, t]);

  const handleSubmit = useCallback(async () => {
    if (!selectedTemplateId || matchCount === null || matchCount === 0) return;

    setSubmitting(true);
    setError(null);
    try {
      const request: BatchGenerateRequest = {
        templateId: selectedTemplateId,
        outputFormat,
        conditionGroups,
        overwriteExisting,
      };
      const result = await customDocumentService.createBatchJob(clientId, request);
      onJobCreated(result);
      onClose();
    } catch {
      setError(t('batch.submitError', 'Failed to create batch job'));
    } finally {
      setSubmitting(false);
    }
  }, [
    clientId, selectedTemplateId, outputFormat, conditionGroups,
    overwriteExisting, matchCount, onJobCreated, onClose, t,
  ]);

  const canSubmit = selectedTemplateId && matchCount !== null && matchCount > 0 && !submitting;

  return (
    <>
      <UploadingModal isOpen={submitting} message={t('batch.creating', 'Creating batch job...')} />
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content batch-modal" onClick={e => e.stopPropagation()}>
          <div className="form-panel-header">
            <h4>{t('batch.title', 'Batch Document Generation')}</h4>
            <button className="btn-close" onClick={onClose}>&times;</button>
          </div>

          <div className="form-panel-body">
            {/* If / Then layout */}
            <div className="batch-if-then-layout">
              {/* IF — Trade Selection Criteria */}
              <div className="batch-if-section">
                <div className="batch-section-label">{t('rules.ifLabel', 'If')}</div>
                <RuleConditionBuilder
                  conditionGroups={conditionGroups}
                  fieldCatalogue={fieldCatalogue}
                  onChange={setConditionGroups}
                />
                <div className="batch-count-row">
                  <button
                    className="btn-secondary"
                    onClick={handleCount}
                    disabled={counting}
                  >
                    {counting
                      ? t('batch.counting', 'Counting...')
                      : t('batch.countButton', 'Count Matching Trades')
                    }
                  </button>
                  {matchCount !== null && (
                    <span className={`batch-count-result ${matchCount === 0 ? 'batch-count-zero' : ''}`}>
                      {matchCount === 0
                        ? t('batch.noMatches', 'No trades match the current criteria.')
                        : t('batch.matchCount', '{{count}} trades match these criteria', { count: matchCount })
                      }
                    </span>
                  )}
                </div>
                {matchCount !== null && matchCount > 500 && (
                  <p className="form-hint batch-large-warning">
                    {t('batch.largeWarning', 'Large batch — this may take a while to process.')}
                  </p>
                )}
              </div>

              <div className="batch-then-arrow">→</div>

              {/* THEN — Template, Format, Options */}
              <div className="batch-then-section">
                <div className="batch-section-label">{t('rules.thenLabel', 'Then')}</div>

                <div className="form-group">
                  <label>{t('batch.template', 'Template')}</label>
                  <select
                    className="form-input"
                    value={selectedTemplateId}
                    onChange={e => setSelectedTemplateId(e.target.value)}
                  >
                    <option value="">{t('batch.selectTemplate', '— Select template —')}</option>
                    {activeTemplates.map(tmpl => (
                      <option key={tmpl.templateId} value={tmpl.templateId}>
                        {tmpl.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>{t('batch.format', 'Format')}</label>
                  <div className="batch-format-radios">
                    <label className="batch-radio-label">
                      <input
                        type="radio"
                        name="outputFormat"
                        value="PDF"
                        checked={outputFormat === 'PDF'}
                        onChange={() => setOutputFormat('PDF')}
                      />
                      PDF
                    </label>
                    <label className="batch-radio-label">
                      <input
                        type="radio"
                        name="outputFormat"
                        value="DOCX"
                        checked={outputFormat === 'DOCX'}
                        onChange={() => setOutputFormat('DOCX')}
                      />
                      DOCX
                    </label>
                  </div>
                </div>

                <label className="batch-checkbox-label">
                  <input
                    type="checkbox"
                    checked={overwriteExisting}
                    onChange={e => setOverwriteExisting(e.target.checked)}
                  />
                  {t('batch.overwriteExisting', 'Overwrite existing documents for the same template')}
                </label>
                {overwriteExisting && (
                  <p className="form-hint batch-overwrite-warning">
                    {t('batch.overwriteWarning', 'Previous documents for matching trades will be marked as superseded.')}
                  </p>
                )}
              </div>
            </div>

            {error && <div className="form-error">{error}</div>}
          </div>

          <div className="form-panel-footer">
            <button className="btn-secondary" onClick={onClose} disabled={submitting}>
              {t('common.cancel', 'Cancel')}
            </button>
            <button
              className="btn-primary"
              onClick={handleSubmit}
              disabled={!canSubmit}
            >
              {t('batch.startButton', 'Start Batch')}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default BatchGenerateModal;
